import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  attachApiErrorToast,
  getApiErrorMessage,
  getNotificationErrorMessage,
} from "../../helpers/apiNotify";
import { showError } from "../../helpers/toast";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});
attachApiErrorToast(API, { fallback: "Notification request failed" });

const authHeaders = (accessToken) => ({
  "ngrok-skip-browser-warning": "69420",
  Authorization: `${accessToken}`,
  "Content-Type": "application/json",
});

/** Build POST body with only defined audience flags (avoids sending undefined). */
const buildNotificationBody = ({
  message,
  userId,
  is_admin,
  subject,
  htmlMailContent,
  sendToGamingBoardAdmins,
  sendToGamingBoardManagers,
  sendToGamingBoardViewers,
  sendToGamingBoardReviewers,
  sendToCompanyAdmins,
}) => {
  const body = {
    message,
    userId,
    is_admin: Boolean(is_admin),
  };
  if (subject != null) body.subject = subject;
  if (htmlMailContent != null) body.htmlMailContent = htmlMailContent;
  if (sendToGamingBoardAdmins) body.sendToGamingBoardAdmins = true;
  if (sendToGamingBoardManagers) body.sendToGamingBoardManagers = true;
  if (sendToGamingBoardViewers) body.sendToGamingBoardViewers = true;
  if (sendToGamingBoardReviewers) body.sendToGamingBoardReviewers = true;
  if (sendToCompanyAdmins) body.sendToCompanyAdmins = true;
  return body;
};

const toRejectPayload = (error, fallback) => {
  const status = error?.response?.status;
  const message = getNotificationErrorMessage(error, fallback);
  return {
    message,
    status,
    accessDenied: status === 403 || /access denied/i.test(String(message)),
    data: error?.response?.data,
  };
};

/** Toasts are handled by attachApiErrorToast; keep helper for validation-only errors */
const toastNotificationError = (error, fallback) => {
  // Interceptor already toasted axios errors; only toast synthetic payloads (no response)
  if (error?.response) return;
  showError(getNotificationErrorMessage(error, fallback));
};

// Async thunk for fetching notifications
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async ({ accessToken, queryParams = {}, silent = true }, { rejectWithValue }) => {
    try {
      let url = "/notification/";

      const params = { ...queryParams };
      if (!params.limit) params.limit = 30;
      if (!params.page) params.page = 1;

      if (Object.keys(params).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            searchParams.append(key, value);
          }
        });
        url = `${url}?${searchParams.toString()}`;
      }

      const response = await API.get(url, {
        headers: authHeaders(accessToken),
        // List refresh after create should not toast; notifications page sets silent:false
        skipErrorToast: Boolean(silent),
      });
      return response.data;
    } catch (error) {
      const payload = toRejectPayload(error, "Failed to fetch notifications");
      // Toast when not silent (list page). 403 already toasted by interceptor unless skipped.
      if (!silent) {
        toastNotificationError(error, "Failed to fetch notifications");
      }
      return rejectWithValue(payload);
    }
  }
);

// Async thunk for creating a notification
export const createNotification = createAsyncThunk(
  "notifications/createNotification",
  async (args, { rejectWithValue, dispatch }) => {
    const {
      message,
      userId,
      accessToken,
      is_admin,
      subject,
      htmlMailContent,
      sendToGamingBoardAdmins,
      sendToGamingBoardManagers,
      sendToGamingBoardViewers,
      sendToGamingBoardReviewers,
      sendToCompanyAdmins,
    } = args || {};

    if (!accessToken) {
      const payload = {
        message: "401: Missing access token for notification",
        status: 401,
      };
      toastNotificationError(payload, payload.message);
      return rejectWithValue(payload);
    }
    if (userId == null || userId === "") {
      const payload = {
        message: "400: Missing userId for notification",
        status: 400,
      };
      toastNotificationError(payload, payload.message);
      return rejectWithValue(payload);
    }

    try {
      const config = { headers: authHeaders(accessToken) };
      const body = buildNotificationBody({
        message,
        userId,
        is_admin,
        subject,
        htmlMailContent,
        sendToGamingBoardAdmins,
        sendToGamingBoardManagers,
        sendToGamingBoardViewers,
        sendToGamingBoardReviewers,
        sendToCompanyAdmins,
      });

      const response = await API.post("/notification/", body, config);

      // Refresh list (ignore fetch failures so create success is preserved)
      try {
        await dispatch(fetchNotifications({ accessToken }));
      } catch (_) {
        /* ignore */
      }

      // Email is best-effort; do not fail notification create if mail fails
      try {
        const mailBody = {
          userId,
          subject: subject || "Notification",
          htmlMailContent:
            htmlMailContent ||
            "<p>check your account your application status is updated</p>",
        };
        if (sendToGamingBoardAdmins) mailBody.sendToGamingBoardAdmins = true;
        if (sendToGamingBoardManagers) mailBody.sendToGamingBoardManagers = true;
        if (sendToGamingBoardViewers) mailBody.sendToGamingBoardViewers = true;
        if (sendToGamingBoardReviewers) mailBody.sendToGamingBoardReviewers = true;
        if (sendToCompanyAdmins) mailBody.sendToCompanyAdmins = true;

        await API.post("/users/sendMail", mailBody, config);
      } catch (emailError) {
        const mailMsg = getApiErrorMessage(emailError, "Failed to send notification email");
        console.error(
          `[notification email] ${emailError?.response?.status || "network"}:`,
          mailMsg,
          emailError?.response?.data
        );
      }

      return response.data;
    } catch (error) {
      const payload = toRejectPayload(error, "Failed to create notification");
      // Toast API response message with status (e.g. "403: …")
      toastNotificationError(error, "Failed to create notification");
      console.error(
        `[createNotification] ${payload.status}:`,
        payload.message,
        error?.response?.data
      );
      return rejectWithValue(payload);
    }
  }
);

// Async thunk for updating a notification
export const updateNotification = createAsyncThunk(
  "notifications/updateNotification",
  async (
    { notificationId, message, read_status, accessToken },
    { rejectWithValue }
  ) => {
    try {
      const response = await API.put(
        `/notification/${notificationId}`,
        { message, read_status },
        { headers: authHeaders(accessToken) }
      );
      return response.data;
    } catch (error) {
      const payload = toRejectPayload(error, "Failed to update notification");
      toastNotificationError(error, "Failed to update notification");
      return rejectWithValue(payload);
    }
  }
);

// Async thunk for deleting a notification
export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async ({ configdata }, { rejectWithValue }) => {
    try {
      await API.delete(`/notification/${configdata.notification_ids}`, {
        headers: authHeaders(configdata.accessToken),
      });
      return configdata.notification_ids;
    } catch (error) {
      const payload = toRejectPayload(error, "Failed to delete notification");
      toastNotificationError(error, "Failed to delete notification");
      return rejectWithValue(payload);
    }
  }
);

const initialState = {
  notifications: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30,
  },
  unreadCount: 0,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.notifications.findIndex(
          (notification) => notification.id === action.payload.id
        );
        if (index !== -1) {
          state.notifications[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = state.notifications.filter(
          (notification) => notification.id !== action.payload
        );
        state.error = null;
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;

        const { page = 1, limit = 30 } = action.meta.arg.queryParams || {};

        if (Array.isArray(action.payload)) {
          const totalItems = action.payload.length;
          const totalPages = Math.ceil(totalItems / limit) || 1;
          const startIndex = (page - 1) * limit;
          const endIndex = startIndex + limit;

          state.notifications = action.payload.slice(startIndex, endIndex);
          state.pagination = {
            currentPage: Number(page),
            totalPages,
            totalItems,
            itemsPerPage: Number(limit),
          };
        } else {
          state.notifications = action.payload.data || [];
          state.unreadCount = action.payload.unreadCount || 0;

          if (action.payload.pagination) {
            state.pagination = {
              currentPage: action.payload.pagination.page || 1,
              totalPages: action.payload.pagination.totalPages || 1,
              totalItems: action.payload.pagination.total || 0,
              itemsPerPage: action.payload.pagination.limit || limit || 30,
            };
          } else {
            state.pagination = {
              currentPage: 1,
              totalPages: 1,
              totalItems: state.notifications.length,
              itemsPerPage: state.notifications.length,
            };
          }
        }

        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
      });
  },
});

export default notificationSlice.reducer;
